import React from 'react';
import Panel from '../../components/ui/Panel';
import CodeBlock from '../../components/ui/CodeBlock';

const BordersPage: React.FC = () => {
  const tailwindConfigCode = `// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      borderWidth: {
        '3': '3px',
      },
      borderColor: {
        'osrs-border': '#474745',
        'osrs-gold': '#E6A519',
        'osrs-dark-brown': '#382D1A',
      },
    },
  },
}`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Borders</h1>
      <p className="text-osrs-yellow mb-8">
        Borders are a defining characteristic of the OSRS interface. The thick, pixel-perfect borders
        help create the distinctive nostalgic look of Old School RuneScape.
      </p>
      
      <section id="styles" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Border Styles</h2>
        <p className="text-osrs-yellow mb-6">
          The OSRS Design System uses several border styles to define UI elements and create visual hierarchy.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Border Widths</h3>
            
            <div className="space-y-4">
              <div className="p-2">
                <div className="border border-osrs-border bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">1px Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">Standard thin border (border)</p>
              </div>
              
              <div className="p-2">
                <div className="border-2 border-osrs-border bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">2px Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">Medium border (border-2)</p>
              </div>
              
              <div className="p-2">
                <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">3px Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">OSRS standard thick border (border-3)</p>
              </div>
              
              <div className="p-2">
                <div className="border-4 border-osrs-border bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">4px Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">Extra thick border (border-4)</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Border Colors</h3>
            
            <div className="space-y-4">
              <div className="p-2">
                <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">Standard Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">border-osrs-border (#474745)</p>
              </div>
              
              <div className="p-2">
                <div className="border-3 border-osrs-gold bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">Gold Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">border-osrs-gold (#E6A519)</p>
              </div>
              
              <div className="p-2">
                <div className="border-3 border-osrs-dark-brown bg-osrs-panel p-4 mb-2">
                  <p className="text-osrs-yellow">Dark Brown Border</p>
                </div>
                <p className="text-osrs-yellow text-sm">border-osrs-dark-brown (#382D1A)</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8">
          <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Border Radius</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-2">
              <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2 rounded-none">
                <p className="text-osrs-yellow">No Radius</p>
              </div>
              <p className="text-osrs-yellow text-sm">rounded-none (0px)</p>
            </div>
            
            <div className="p-2">
              <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2 rounded-sm">
                <p className="text-osrs-yellow">Small Radius</p>
              </div>
              <p className="text-osrs-yellow text-sm">rounded-sm (2px)</p>
            </div>
            
            <div className="p-2">
              <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2 rounded">
                <p className="text-osrs-yellow">Default Radius</p>
              </div>
              <p className="text-osrs-yellow text-sm">rounded (4px)</p>
            </div>
            
            <div className="p-2">
              <div className="border-3 border-osrs-border bg-osrs-panel p-4 mb-2 rounded-md">
                <p className="text-osrs-yellow">Medium Radius</p>
              </div>
              <p className="text-osrs-yellow text-sm">rounded-md (6px)</p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Bevel</h3>
          <p className="text-osrs-yellow mb-4">
            Windows and buttons are square. The light edge sits on the top and left. The dark edge sits on the bottom and right. Slots and inputs invert that bevel.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="osrs-bevel p-4 text-osrs-yellow" style={{ background: '#3E3530' }}>Raised window</div>
            <div className="osrs-inset p-4 text-osrs-yellow" style={{ background: '#1A1612' }}>Inset slot</div>
          </div>
        </div>
      </section>

      <section id="implementation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Implementation</h2>
        
        <Panel title="Tailwind Config" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Extend Tailwind's border configurations for OSRS-specific values:
          </p>
          <CodeBlock code={tailwindConfigCode} language="jsx" />
        </Panel>
        
        <Panel title="CSS Classes" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Common border utility classes:
          </p>
          <div className="space-y-2">
            <p className="text-osrs-yellow"><code>border-3</code> - 3px border width</p>
            <p className="text-osrs-yellow"><code>border-osrs-border</code> - Default OSRS border color</p>
            <p className="text-osrs-yellow"><code>border-osrs-gold</code> - Gold border for emphasis</p>
            <p className="text-osrs-yellow"><code>rounded</code> - 4px border radius (recommended)</p>
          </div>
        </Panel>
      </section>
      
      <section id="usage" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Best Practices</h3>
          <p className="text-osrs-yellow mb-2">
            Follow these principles when using borders in your OSRS-styled UI:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use 3px (border-3) borders for main container elements to maintain the authentic OSRS look</li>
            <li>Choose border-osrs-border (#474745) for most UI elements</li>
            <li>Use gold borders to highlight important or interactive elements</li>
            <li>Apply a subtle border radius (rounded) to soften container edges</li>
            <li>Maintain consistency in border styles throughout the interface</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Component-Specific Borders</h3>
          <p className="text-osrs-yellow mb-2">
            Different components in OSRS use specific border styles:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Panels: 3px border with the standard border color</li>
            <li>Buttons: 3px border with a color that contrasts with the button background</li>
            <li>Inventory slots: 1-2px borders to define the grid</li>
            <li>Dialog boxes: 3px gold borders to indicate importance</li>
            <li>Input fields: 2-3px borders that change color on focus</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default BordersPage; 